/**
 * STANLEY CUSTOM: <vista-360> — giro 360° de producto a partir de un video.
 *
 * Cómo se dibuja:
 * - El <video> es solo la fuente (opacidad 0). Cada cuadro se pinta en un <canvas>.
 *   Mostrar el video directo lo mandaba a una capa de hardware (overlay) que, con las
 *   esquinas redondeadas de la galería, dejaba líneas negras en el borde (que además
 *   no salían en las capturas de pantalla).
 *
 * Arrastre:
 * - Saltar dentro del video es lento cuando tiene pocos keyframes (Shopify deja 2 en
 *   3,84 s → cada salto re-decodifica hasta ~48 cuadros). Mientras el video gira solo
 *   se guarda una copia chica de cada uno de los SLOTS cuadros; al arrastrar se
 *   pintan esas copias (instantáneo) y al soltar, el cuadro exacto en alta.
 * - Con mouse, además, se pide el cuadro real en paralelo y reemplaza a la copia si el
 *   puntero sigue ahí (nítido mientras se arrastra). En táctil no: el teléfono se traba.
 * - Si se arrastra antes de completar la primera vuelta, los huecos se muestran con el
 *   cuadro guardado más cercano y se van llenando con saltos de a uno.
 *
 * Rendimiento:
 * - No baja nada hasta que la imagen fija se pintó, el navegador está libre Y la
 *   media está a la vista. Las de otros colores están en display:none → nunca se cargan.
 * - Elige el mp4 más chico que alcance para el ancho en pantalla.
 * - Con "ahorro de datos" o "reducir movimiento" no arranca solo.
 * - Se pausa fuera de pantalla y con la pestaña oculta.
 */
const SLOTS = 48; // cuadros guardados por vuelta (mobile)
// Lado máximo de cada copia guardada: 480 px en táctil (≈ 44 MB las 48), 600 px con mouse (≈ 69 MB).
const CACHE_MAX_PX = window.matchMedia('(pointer: coarse)').matches ? 480 : 600;

const whenIdle = () =>
  new Promise((resolve) => {
    if ('requestIdleCallback' in window) requestIdleCallback(() => resolve(), { timeout: 2000 });
    else setTimeout(resolve, 300);
  });

const posterReady = (img) =>
  !img || img.complete
    ? Promise.resolve()
    : new Promise((resolve) => {
        img.addEventListener('load', resolve, { once: true });
        img.addEventListener('error', resolve, { once: true });
      });

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const saveData = () => Boolean(navigator.connection && navigator.connection.saveData);
const coarsePointer = () => window.matchMedia('(pointer: coarse)').matches;

class Vista360 extends HTMLElement {
  #video;
  #canvas;
  #ctx;
  #toggle;
  #io;
  #controller = new AbortController();
  #visible = false;
  #wantsPlay = !reducedMotion() && !saveData();
  #loadedPromise = null;

  /** Copias por slot (solo mobile). */
  #cache = new Array(SLOTS).fill(null);
  #useCache = true;
  /** Con mouse los saltos son baratos: se piden en paralelo para reemplazar la copia por el cuadro nítido. */
  #sharpenWhileDragging = !coarsePointer();
  #cacheCtx = null;

  /** Posición actual del giro, en vueltas (0..1). */
  #pos = 0;
  /** Salto pendiente dentro del video (se hace de a uno). */
  #seekBusy = false;
  #seekTarget = null;

  connectedCallback() {
    this.#video = this.querySelector('video');
    this.#canvas = this.querySelector('canvas');
    this.#ctx = this.#canvas.getContext('2d', { alpha: false });
    this.#toggle = this.querySelector('.vista-360__toggle');
    const { signal } = this.#controller;

    this.#syncToggle();
    this.#toggle.addEventListener(
      'click',
      (e) => {
        e.stopPropagation();
        this.#wantsPlay = this.#video.paused;
        this.classList.add('was-touched');
        this.#ensureLoaded().then(() => (this.#wantsPlay ? this.#play() : this.#video.pause()));
      },
      { signal }
    );
    // Que tocar el botón no arranque un arrastre ni lo tome el carrusel.
    this.#toggle.addEventListener('pointerdown', (e) => e.stopPropagation(), { signal });

    this.#video.addEventListener('play', () => { this.#syncToggle(); this.#loop(); }, { signal });
    this.#video.addEventListener('pause', () => this.#syncToggle(), { signal });
    this.#video.addEventListener('seeked', this.#onSeeked, { signal });

    // El carrusel arrastra con mousedown: lo frenamos acá para que el arrastre gire el termo.
    this.addEventListener('mousedown', (e) => e.stopPropagation(), { signal });
    this.addEventListener('pointerdown', this.#onPointerDown, { signal });
    this.addEventListener('touchstart', this.#onTouchStart, { signal, passive: true });
    this.addEventListener('touchmove', this.#onTouchMove, { signal, passive: false });
    this.addEventListener('touchend', this.#onTouchEnd, { signal });
    this.addEventListener('touchcancel', this.#onTouchEnd, { signal });
    this.addEventListener('keydown', this.#onKeyDown, { signal });
    this.addEventListener('click', (e) => e.stopPropagation(), { signal });

    document.addEventListener(
      'visibilitychange',
      () => {
        if (document.hidden) this.#video.pause();
        else if (this.#visible && this.#wantsPlay) this.#play();
      },
      { signal }
    );

    this.#io = new IntersectionObserver(
      ([entry]) => {
        this.#visible = entry.isIntersecting;
        if (!this.#visible) return this.#video.pause();
        if (!this.#wantsPlay) return;
        posterReady(this.parentElement.querySelector('.vista-360__poster'))
          .then(whenIdle)
          .then(() => this.#visible && this.#wantsPlay && this.#ensureLoaded())
          .then(() => this.#visible && this.#wantsPlay && this.#play());
      },
      { threshold: 0.4 }
    );
    this.#io.observe(this);
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#io?.disconnect();
    this.#cache.forEach((c) => c?.close?.());
    this.#cache.fill(null);
  }

  // ---------- Carga ----------

  /** Elige la fuente mp4 más chica que cubra el ancho en pantalla × densidad. */
  #pickSource() {
    const urls = (this.dataset.sources || '').split('|').filter(Boolean);
    const heights = (this.dataset.heights || '').split('|').map(Number);
    if (urls.length <= 1 || heights.length !== urls.length) return urls[0];
    const need = this.getBoundingClientRect().width * Math.min(window.devicePixelRatio || 1, 2);
    const options = urls.map((url, i) => ({ url, h: heights[i] })).sort((a, b) => a.h - b.h);
    return (options.find((o) => o.h >= need) || options[options.length - 1]).url;
  }

  #ensureLoaded() {
    if (this.#loadedPromise) return this.#loadedPromise;
    const v = this.#video;
    v.src = this.#pickSource();
    v.preload = 'auto';
    this.#loadedPromise = new Promise((resolve) => {
      v.addEventListener('loadeddata', () => { this.#sizeCanvas(); this.#draw(); resolve(); }, { once: true });
    });
    v.load();
    return this.#loadedPromise;
  }

  #sizeCanvas() {
    const v = this.#video;
    const rect = this.getBoundingClientRect();
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const side = Math.round(Math.min(Math.max(rect.width * dpr, 1), v.videoWidth || 1080));
    this.#canvas.width = side;
    this.#canvas.height = Math.round(side * ((v.videoHeight || 1) / (v.videoWidth || 1)));
  }

  // ---------- Dibujo ----------

  #slotOf(time) {
    const d = this.#video.duration || 1;
    return Math.round((time / d) * SLOTS) % SLOTS;
  }

  /** Pinta el cuadro actual del video en el canvas y, si falta, guarda la copia de su slot. */
  #draw() {
    const v = this.#video;
    if (v.readyState < 2) return;
    this.#ctx.drawImage(v, 0, 0, this.#canvas.width, this.#canvas.height);
    this.classList.add('is-ready');
    if (this.#useCache) this.#store(this.#slotOf(v.currentTime));
  }

  #store(slot) {
    if (this.#cache[slot]) return;
    const v = this.#video;
    const side = Math.min(CACHE_MAX_PX, this.#canvas.width);
    if (!this.#cacheCtx) {
      const c = document.createElement('canvas');
      c.width = side;
      c.height = Math.round(side * (this.#canvas.height / this.#canvas.width));
      this.#cacheCtx = c.getContext('2d', { alpha: false });
    }
    const c = this.#cacheCtx.canvas;
    this.#cacheCtx.drawImage(v, 0, 0, c.width, c.height);
    this.#cache[slot] = 'pending';
    createImageBitmap(c)
      .then((bmp) => (this.#cache[slot] = bmp))
      .catch(() => (this.#cache[slot] = null));
  }

  /** Pinta una copia guardada (la del slot o la más cercana). Devuelve si encontró el slot exacto. */
  #drawCached(slot) {
    for (let d = 0; d <= SLOTS / 2; d++) {
      for (const s of d ? [slot + d, slot - d] : [slot]) {
        const bmp = this.#cache[((s % SLOTS) + SLOTS) % SLOTS];
        if (bmp && bmp !== 'pending') {
          this.#ctx.drawImage(bmp, 0, 0, this.#canvas.width, this.#canvas.height);
          this.classList.add('is-ready');
          return d === 0;
        }
      }
    }
    return false;
  }

  /** Mientras reproduce, pinta cada cuadro (requestVideoFrameCallback si existe). */
  #loop = () => {
    const v = this.#video;
    if (v.paused || v.ended) return;
    this.#draw();
    this.#pos = (v.currentTime / (v.duration || 1)) % 1;
    if (v.requestVideoFrameCallback) v.requestVideoFrameCallback(this.#loop);
    else requestAnimationFrame(this.#loop);
  };

  #play() {
    this.#video.play().catch(() => {});
  }

  #syncToggle() {
    const playing = !this.#video.paused;
    this.classList.toggle('is-playing', playing);
    this.#toggle.setAttribute('aria-label', playing ? 'Pausar giro' : 'Girar');
  }

  // ---------- Saltos dentro del video (de a uno) ----------

  #seekTo(turns) {
    this.#seekTarget = turns;
    if (!this.#seekBusy) this.#nextSeek();
  }

  #nextSeek() {
    const v = this.#video;
    if (this.#seekTarget === null || !v.duration) return;
    const t = this.#seekTarget * v.duration;
    this.#seekTarget = null;
    this.#seekBusy = true;
    v.currentTime = Math.min(t, v.duration - 0.001);
  }

  #onSeeked = () => {
    this.#seekBusy = false;
    if (this.classList.contains('is-dragging')) {
      // Arrastrando: el salto llena la copia de su cuadro. Si el puntero sigue en ese
      // cuadro se pinta el real (nítido); si ya se fue, la copia de donde está ahora
      // (nunca un cuadro que quedó atrás).
      const seekSlot = this.#slotOf(this.#video.currentTime);
      const nowSlot = Math.round(this.#pos * SLOTS) % SLOTS;
      this.#store(seekSlot);
      if (seekSlot === nowSlot) this.#ctx.drawImage(this.#video, 0, 0, this.#canvas.width, this.#canvas.height);
      else this.#drawCached(nowSlot);
    } else {
      this.#draw();
    }
    this.#nextSeek();
  };

  // ---------- Interacción ----------

  #rotateBy(turns, { final = false } = {}) {
    this.#pos = (((this.#pos + turns) % 1) + 1) % 1;
    if (!this.#video.duration) return;
    if (this.#useCache && !final) {
      const exact = this.#drawCached(Math.round(this.#pos * SLOTS) % SLOTS);
      // Llenar el hueco con el cuadro real; con mouse, además, afinar a la versión nítida.
      if (!exact || this.#sharpenWhileDragging) this.#seekTo(this.#pos);
    } else {
      this.#seekTo(this.#pos);
    }
  }

  #stopAuto() {
    this.#wantsPlay = false;
    this.#video.pause();
    this.#pos = (this.#video.currentTime / (this.#video.duration || 1)) % 1;
    this.classList.add('was-touched');
  }

  // Arrastre: misma lógica para mouse y dedo, distinta entrada.
  #drag = null;

  #dragStart(x) {
    this.#stopAuto();
    this.classList.add('is-dragging');
    this.#ensureLoaded();
    this.#drag = { lastX: x, pending: 0, raf: 0, width: this.getBoundingClientRect().width || 1 };
  }

  #dragMove(x) {
    const d = this.#drag;
    if (!d) return;
    // Arrastrar hacia la izquierda gira en el mismo sentido que el video. Un ancho = una vuelta.
    d.pending += (d.lastX - x) / d.width;
    d.lastX = x;
    if (!d.raf)
      d.raf = requestAnimationFrame(() => {
        d.raf = 0;
        this.#rotateBy(d.pending);
        d.pending = 0;
      });
  }

  #dragEnd() {
    if (!this.#drag) return;
    this.#drag = null;
    this.classList.remove('is-dragging');
    // Al soltar: el cuadro exacto, en alta.
    this.#rotateBy(0, { final: true });
  }

  /** Mouse / lápiz. El dedo va por los eventos táctiles (abajo). */
  #onPointerDown = (e) => {
    if (e.pointerType === 'touch' || e.button !== 0) return;
    this.#dragStart(e.clientX);
    try {
      this.setPointerCapture(e.pointerId);
    } catch {
      /* sin captura igual funciona mientras el puntero siga encima */
    }
    const move = (ev) => this.#dragMove(ev.clientX);
    const end = (ev) => {
      this.removeEventListener('pointermove', move);
      this.removeEventListener('pointerup', end);
      this.removeEventListener('pointercancel', end);
      this.#dragEnd();
    };
    this.addEventListener('pointermove', move);
    this.addEventListener('pointerup', end);
    this.addEventListener('pointercancel', end);
  };

  /**
   * Dedo. En iPhone (Safari y Chrome, ambos WebKit) el navegador cancelaba los pointer
   * events a los ~30 px porque tomaba el gesto como scroll (diagnóstico en iPhone:
   * 16 de 16 arrastres cortados). Con touch events decidimos la dirección en los
   * primeros px: si es horizontal, preventDefault en cada touchmove (listener no
   * pasivo) y el gesto es nuestro; si es vertical, lo soltamos y la página scrollea.
   */
  #touch = null;
  static #LOCK_PX = 6;

  #onTouchStart = (e) => {
    if (e.touches.length !== 1 || e.target.closest('.vista-360__toggle')) return;
    const t = e.touches[0];
    this.#touch = { id: t.identifier, x0: t.clientX, y0: t.clientY, axis: null };
  };

  #onTouchMove = (e) => {
    const s = this.#touch;
    if (!s) return;
    const t = [...e.changedTouches].find((c) => c.identifier === s.id);
    if (!t) return;
    if (!s.axis) {
      const dx = Math.abs(t.clientX - s.x0);
      const dy = Math.abs(t.clientY - s.y0);
      if (Math.max(dx, dy) < Vista360.#LOCK_PX) return;
      s.axis = dx >= dy ? 'x' : 'y';
      if (s.axis === 'y') return (this.#touch = null); // scroll vertical: que lo haga la página
      this.#dragStart(s.x0);
    }
    if (e.cancelable) e.preventDefault(); // el gesto horizontal es del 360, no del carrusel ni de la página
    this.#dragMove(t.clientX);
  };

  #onTouchEnd = (e) => {
    const s = this.#touch;
    if (!s || ![...e.changedTouches].some((c) => c.identifier === s.id)) return;
    this.#touch = null;
    if (s.axis === 'x') this.#dragEnd();
  };

  #onKeyDown = (e) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    e.stopPropagation();
    this.#stopAuto();
    this.#ensureLoaded().then(() => this.#rotateBy(e.key === 'ArrowLeft' ? 1 / SLOTS : -1 / SLOTS, { final: true }));
  };
}

if (!customElements.get('vista-360')) customElements.define('vista-360', Vista360);
